import { useCardError } from "@components/HomePage/CardParts/CardError";
import CardStateFooter from "@components/HomePage/CardParts/CardStateFooter";
import useAvailableCatalogClone from "@components/HomePage/CardParts/useAvailableCatalogClone";
import useAvailableCatalogLogo from "@components/HomePage/CardParts/useAvailableCatalogLogo";
import { useCanCloneAvailable, useCanManageClone } from "@components/HomePage/CardParts/useCanManageClone";
import useCardState from "@components/HomePage/CardParts/useCardState";
import useLoadedCatalogLogo from "@components/HomePage/CardParts/useLoadedCatalogLogo";
import Url from "@core-ui/ApiServices/Types/Url";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import t from "@ext/localization/locale/translate";
import { type CatalogLink, isCloningStatus, isNotLoadedStatus } from "@ext/navigation/NavigationLinks";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { memo } from "react";
import Link from "../Atoms/Link";
import CardView from "./CardView";

interface CardProps {
	link: CatalogLink;
	onClick: () => void;
	className?: string;
	name: string;
}

const GxCard = ({ link, className, onClick, name }: CardProps) => {
	const { isStatic } = usePlatform();
	const { state, isCloning, progress, error, isCancel, setIsCancel, markOpening } = useCardState(link);
	const canFetchLogo = !isNotLoadedStatus(link.status) && !isCloningStatus(link.status) && !isCloning && !error;
	const loadedLogo = useLoadedCatalogLogo(canFetchLogo ? link.name : undefined);
	const availableLogo = useAvailableCatalogLogo(link);
	const logo = isNotLoadedStatus(link.status) ? availableLogo : loadedLogo;
	const { onClick: onClickError } = useCardError(link, error);
	const canManageClone = useCanManageClone(link.name);
	const canCloneAvailable = useCanCloneAvailable(link.name);
	const { start: startAvailableClone, fallback: availableCloneFallback } = useAvailableCatalogClone(link);

	if (isCancel && !isCloning && !error) return null;

	const pathname = link.lastVisited || link.pathname;

	const card = (
		<CardView
			className={className}
			footer={
				<CardStateFooter
					canManageClone={canManageClone}
					isCancel={isCancel}
					link={link}
					onCloneClick={canCloneAvailable ? startAvailableClone : undefined}
					onErrorClick={onClickError}
					progress={progress}
					setIsCancel={setIsCancel}
					state={state}
				/>
			}
			link={link}
			logo={logo}
			onCardClick={() => {
				onClick();
				markOpening();
			}}
			onErrorClick={canManageClone ? onClickError : undefined}
			state={state}
		/>
	);

	if (state === "clone-error") {
		// Only a user who may manage the clone gets the details dialog; the others just see the card.
		if (!canManageClone) return card;
		return (
			<Tooltip>
				<TooltipTrigger asChild>{card}</TooltipTrigger>
				<TooltipContent>
					<span>{t("clickToViewDetails")}</span>
				</TooltipContent>
			</Tooltip>
		);
	}

	if (isCloningStatus(state)) return card;

	if (state === "not-loaded")
		return (
			<div data-catalog-card={name} data-qa="not-loaded-card">
				{card}
				{availableCloneFallback}
			</div>
		);

	if (isStatic)
		return (
			<a data-catalog-card={name} href={pathname}>
				{card}
			</a>
		);

	if (typeof window === "undefined") return card;
	return (
		<Link data-catalog-card={name} href={Url.from({ pathname })}>
			{card}
		</Link>
	);
};

/**
 * Memoized because every drag move re-renders the sortable wrapper around it, and a card is expensive: clone progress
 * polling, logo resolution, card-store and workspace subscriptions. Callers must keep `onClick` identity-stable.
 */
export default memo(GxCard);
